"use client";

import { useState, useEffect } from "react";
import FeedbackDialog from "@/components/feedback/feedback-dialog";
import { useTranslations, useLocale } from "next-intl";
import { checkFeedbackEligibility } from "@/utils/feedback-tracking";
import { toast } from "sonner";
import { isRTL } from "@/config/site";

export default function FeedbackButton() {
    const locale = useLocale();
    const t = useTranslations("FeedbackButton");
    const [isEligible, setIsEligible] = useState<boolean | null>(null);
    const [showDialog, setShowDialog] = useState(false);
    const [loading, setLoading] = useState(true);
    const [waitingForVerification, setWaitingForVerification] = useState<boolean>(false);
    const rtl = isRTL(locale);

    useEffect(() => {
        checkEligibility();
    }, []);

    const checkEligibility = async () => {
        setLoading(true);
        try {
            const { eligible, reason, waitingForVerification } = await checkFeedbackEligibility();
            setIsEligible(eligible);
            setWaitingForVerification(waitingForVerification ?? false);
    
            if (!eligible && reason) {
                toast.error(reason);
            }
        } catch (error) {
            console.error("Error checking eligibility:", error);
            setIsEligible(false);
        } finally {
            setLoading(false);
        }
    };
    
    const handleClick = async () => {
        if (loading) return;

        if (isEligible) {
            setShowDialog(true);
        } else {
            const { eligible, reason } = await checkFeedbackEligibility();
            if (eligible) {
                setIsEligible(true);
                setShowDialog(true);
            } else {
                toast.error(reason || t("notEligible"));
            }
        }
    };

    if (loading) return null;
    if (!isEligible) return null;

    // CSS khusus untuk memastikan tombol terlihat di kedua mode
    // KOREKSI: LTR -> posisi kiri, RTL -> posisi kanan
    const tabStyle: React.CSSProperties = {
        position: 'fixed',
        zIndex: 50,
        top: '40%',
        [rtl ? 'right' : 'left']: 0, // Posisi yang dikoreksi
        backgroundColor: 'var(--primary, #007bff)',
        color: 'white',
        padding: '1rem 0.5rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Border radius juga dikoreksi
        borderTopLeftRadius: rtl ? '0.5rem' : 0,
        borderBottomLeftRadius: rtl ? '0.5rem' : 0,
        borderTopRightRadius: rtl ? 0 : '0.5rem',
        borderBottomRightRadius: rtl ? 0 : '0.5rem',
        writingMode: 'vertical-lr', // Konsisten menggunakan vertical-lr
        
        letterSpacing: '1px',
        fontWeight: 500,
        fontSize: '0.875rem',
    };

    return (
        <>
            <button 
                style={tabStyle}
                onClick={handleClick}
            >
                {t("tombol")}
            </button>
            
            <FeedbackDialog 
                open={showDialog} 
                onOpenChange={setShowDialog}
                onSubmitSuccess={() => {
                    setIsEligible(false);
                    setShowDialog(false);
                }}
            />
        </>
    );
}